import { read, utils } from "@sheet/core";
import DOMPurify from "dompurify";
import { fillUnevaluatedFormulaCells } from "@common/helpers/sheet";
import { MAX_XLSX_PREVIEW_ROWS, MAX_XLSX_PREVIEW_COLS } from "./constants";

export interface XlsxPreviewData {
    sheetsHtml: Record<string, string>;
    sheetNames: string[];
    sheetsLimited: Record<string, { rows: boolean; cols: boolean }>;
    hasUnevaluatedFormulas: boolean;
}

export function parseXlsx(buffer: ArrayBuffer): XlsxPreviewData | null {
    const workbook = read(buffer, { type: "array" });
    const sheetNames: string[] = [];
    const sheetsHtml: Record<string, string> = {};
    const sheetsLimited: Record<string, { rows: boolean; cols: boolean }> = {};

    let hasUnevaluatedFormulas = false;

    for (const [index, name] of workbook.SheetNames.entries()) {
        const sheetMeta = workbook.Workbook?.Sheets?.[index];
        const worksheet = workbook.Sheets[name];
        const ref = worksheet["!ref"];

        // Skip hidden sheets and empty sheets
        if (sheetMeta?.Hidden || !ref) {
            continue;
        }

        if (fillUnevaluatedFormulaCells(worksheet)) {
            hasUnevaluatedFormulas = true;
        }

        sheetNames.push(name);
        const range = utils.decode_range(ref);

        // Limit rows and columns to prevent OOM on large files
        const rowsLimited = range.e.r > MAX_XLSX_PREVIEW_ROWS;
        const colsLimited = range.e.c > MAX_XLSX_PREVIEW_COLS;
        const needsLimit = rowsLimited || colsLimited;
        const limitedWorksheet = needsLimit
            ? {
                  ...worksheet,
                  "!ref": utils.encode_range({
                      ...range,
                      e: {
                          r: Math.min(range.e.r, MAX_XLSX_PREVIEW_ROWS),
                          c: Math.min(range.e.c, MAX_XLSX_PREVIEW_COLS),
                      },
                  }),
              }
            : worksheet;

        sheetsHtml[name] = DOMPurify.sanitize(
            utils.sheet_to_html(limitedWorksheet),
        );
        sheetsLimited[name] = { rows: rowsLimited, cols: colsLimited };
    }

    return sheetNames.length > 0
        ? { sheetsHtml, sheetNames, sheetsLimited, hasUnevaluatedFormulas }
        : null;
}
